import { LinearGradient } from 'expo-linear-gradient';
import { useCallback, useEffect, useRef } from 'react';
import {
    type LayoutChangeEvent,
    type StyleProp,
    type ViewStyle,
} from 'react-native';
import Animated, {
    type AnimatedStyle,
    Easing,
    interpolate,
    interpolateColor,
    type SharedValue,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { type OnboardingMoneyMapBoxLayout } from '../../types/OnboardingMoneyMapTypes';

interface AnimatedBoxCornerRadii {
    topLeft: number;
    topRight: number;
    bottomLeft: number;
    bottomRight: number;
}

const BAR_RADIUS = 16;

export const OnboardingMoneyMapPreviewAnimatedBox = ({
    progressYH,
    progressXW,
    progressColor,
    splitTextProgress,
    splitLayout,
    systemLayout,
    splitColor,
    systemColor,
    splitBorderRadius = BAR_RADIUS,
    systemBorderRadius,
    zIndex,
    systemGradientColors,
    anchorBottomTo,
    children,
    splitTopContent,
    splitBottomContent,
    onContentHeight,
    highlightStyle,
    disableAnimations = false,
}: {
    progressYH: SharedValue<number>;
    progressXW: SharedValue<number>;
    progressColor: SharedValue<number>;
    splitTextProgress: SharedValue<number>;
    splitLayout: OnboardingMoneyMapBoxLayout;
    systemLayout: OnboardingMoneyMapBoxLayout;
    splitColor: string;
    systemColor: string;
    splitBorderRadius?: number;
    systemBorderRadius?: number | AnimatedBoxCornerRadii;
    zIndex?: number;
    systemGradientColors?: [string, string];
    /** When provided, this box's bottom edge is pinned to the bottom of the reference box */
    anchorBottomTo?: {
        splitLayout: OnboardingMoneyMapBoxLayout;
        systemLayout: OnboardingMoneyMapBoxLayout;
    };
    children?: React.ReactNode;
    splitTopContent?: React.ReactNode;
    splitBottomContent?: React.ReactNode;
    onContentHeight?: (height: number) => void;
    /** Animated style applied to the outer container (e.g. for highlight opacity). */
    highlightStyle?: StyleProp<AnimatedStyle<StyleProp<ViewStyle>>>;
    disableAnimations?: boolean;
}) => {
    const systemRadii: AnimatedBoxCornerRadii =
        systemBorderRadius == null
            ? {
                  topLeft: splitBorderRadius,
                  topRight: splitBorderRadius,
                  bottomLeft: splitBorderRadius,
                  bottomRight: splitBorderRadius,
              }
            : typeof systemBorderRadius === 'number'
              ? {
                    topLeft: systemBorderRadius,
                    topRight: systemBorderRadius,
                    bottomLeft: systemBorderRadius,
                    bottomRight: systemBorderRadius,
                }
              : systemBorderRadius;

    // Smooth out layout changes to avoid visual snaps when
    // content re-measures after the initial (possibly stale) measurement
    const smoothSystemHeight = useSharedValue(systemLayout.height);
    const smoothSystemY = useSharedValue(systemLayout.y);

    useEffect(() => {
        if (disableAnimations) {
            smoothSystemHeight.value = systemLayout.height;
            smoothSystemY.value = systemLayout.y;
            return;
        }
        const heightSmooth = { duration: 150, easing: Easing.out(Easing.ease) };
        if (smoothSystemHeight.value !== systemLayout.height) {
            smoothSystemHeight.value = withTiming(
                systemLayout.height,
                heightSmooth,
            );
        }
        if (smoothSystemY.value !== systemLayout.y) {
            smoothSystemY.value = withTiming(systemLayout.y, heightSmooth);
        }
    }, [systemLayout.height, systemLayout.y, disableAnimations]);

    const splitSmooth = { duration: 450, easing: Easing.out(Easing.ease) };

    const smoothSplitHeight = useSharedValue(splitLayout.height);
    const smoothSplitY = useSharedValue(splitLayout.y);

    useEffect(() => {
        if (disableAnimations) {
            smoothSplitHeight.value = splitLayout.height;
            smoothSplitY.value = splitLayout.y;
            return;
        }
        if (smoothSplitHeight.value !== splitLayout.height) {
            smoothSplitHeight.value = withTiming(
                splitLayout.height,
                splitSmooth,
            );
        }
        if (smoothSplitY.value !== splitLayout.y) {
            smoothSplitY.value = withTiming(splitLayout.y, splitSmooth);
        }
    }, [splitLayout.height, splitLayout.y, disableAnimations]);

    const smoothAnchorBottom = useSharedValue(
        anchorBottomTo != null
            ? anchorBottomTo.splitLayout.y + anchorBottomTo.splitLayout.height
            : 0,
    );

    useEffect(() => {
        if (anchorBottomTo == null) return;
        const target =
            anchorBottomTo.splitLayout.y + anchorBottomTo.splitLayout.height;
        if (disableAnimations) {
            smoothAnchorBottom.value = target;
            return;
        }
        if (smoothAnchorBottom.value !== target) {
            smoothAnchorBottom.value = withTiming(target, splitSmooth);
        }
    }, [
        anchorBottomTo?.splitLayout.y,
        anchorBottomTo?.splitLayout.height,
        disableAnimations,
    ]);

    const animatedStyle = useAnimatedStyle(() => {
        const height = interpolate(
            progressYH.value,
            [0, 1],
            [smoothSplitHeight.value, smoothSystemHeight.value],
        );

        let top: number;
        if (anchorBottomTo != null) {
            const refBottom = interpolate(
                progressYH.value,
                [0, 1],
                [
                    smoothAnchorBottom.value,
                    anchorBottomTo.systemLayout.y +
                        anchorBottomTo.systemLayout.height,
                ],
            );
            top = refBottom - height;
        } else {
            top = interpolate(
                progressYH.value,
                [0, 1],
                [smoothSplitY.value, smoothSystemY.value],
            );
        }

        return {
            position: 'absolute' as const,
            zIndex: zIndex ?? 0,
            left: interpolate(
                progressXW.value,
                [0, 1],
                [splitLayout.x, systemLayout.x],
            ),
            top,
            width: interpolate(
                progressXW.value,
                [0, 1],
                [splitLayout.width, systemLayout.width],
            ),
            height,
            borderTopLeftRadius: interpolate(
                progressYH.value,
                [0, 1],
                [splitBorderRadius, systemRadii.topLeft],
            ),
            borderTopRightRadius: interpolate(
                progressYH.value,
                [0, 1],
                [splitBorderRadius, systemRadii.topRight],
            ),
            borderBottomLeftRadius: interpolate(
                progressYH.value,
                [0, 1],
                [splitBorderRadius, systemRadii.bottomLeft],
            ),
            borderBottomRightRadius: interpolate(
                progressYH.value,
                [0, 1],
                [splitBorderRadius, systemRadii.bottomRight],
            ),
            backgroundColor: interpolateColor(
                progressColor.value,
                [0, 1],
                [splitColor, systemColor],
            ),
            overflow: 'hidden' as const,
        };
    });

    const contentStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressXW.value, [0.5, 1], [0, 1]),
    }));

    const splitTopStyle = useAnimatedStyle(() => ({
        opacity: splitTextProgress.value,
        transform: [
            {
                translateY: interpolate(
                    splitTextProgress.value,
                    [0, 1],
                    [-20, 0],
                ),
            },
        ],
    }));

    const splitBottomStyle = useAnimatedStyle(() => ({
        opacity: splitTextProgress.value,
        transform: [
            {
                translateY: interpolate(
                    splitTextProgress.value,
                    [0, 1],
                    [20, 0],
                ),
            },
        ],
    }));

    const gradientStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressColor.value, [0, 1], [0, 1]),
    }));

    const onContentHeightRef = useRef(onContentHeight);
    onContentHeightRef.current = onContentHeight;

    const systemWidthRef = useRef(systemLayout.width);
    systemWidthRef.current = systemLayout.width;

    const handleContentLayout = useCallback((e: LayoutChangeEvent) => {
        if (systemWidthRef.current <= 0) return;
        const h = e.nativeEvent.layout.height;
        onContentHeightRef.current?.(h);
    }, []);

    return (
        <Animated.View style={[animatedStyle, highlightStyle]}>
            {systemGradientColors != null && (
                <Animated.View
                    style={[
                        {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        },
                        gradientStyle,
                    ]}
                    pointerEvents="none"
                >
                    <LinearGradient
                        colors={systemGradientColors}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 0, y: 1 }}
                        style={{ flex: 1 }}
                    />
                </Animated.View>
            )}
            {children != null && systemLayout.width > 0 && (
                <Animated.View
                    style={[
                        {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: systemLayout.width,
                            padding: 8,
                        },
                        contentStyle,
                    ]}
                    onLayout={handleContentLayout}
                >
                    {children}
                </Animated.View>
            )}
            {splitTopContent != null && (
                <Animated.View
                    style={[
                        {
                            position: 'absolute',
                            top: 6,
                            left: 0,
                            right: 0,
                            alignItems: 'center',
                        },
                        splitTopStyle,
                    ]}
                    pointerEvents="none"
                >
                    {splitTopContent}
                </Animated.View>
            )}
            {splitBottomContent != null && (
                <Animated.View
                    style={[
                        {
                            position: 'absolute',
                            bottom: 6,
                            left: 0,
                            right: 0,
                            alignItems: 'center',
                        },
                        splitBottomStyle,
                    ]}
                    pointerEvents="none"
                >
                    {splitBottomContent}
                </Animated.View>
            )}
        </Animated.View>
    );
};
